import styles from './transactionReviewTableCard.module.scss';
import Selectors from 'src/utils/selectors';
import React from 'react';
import TransactionDate from 'src/components/atomic/atoms/transactionDate/TransactionDate';
import { TransactionModel } from 'src/types/transaction.model';
import TransactionNameAccount from 'src/components/atomic/atoms/transactionNameAccount/TransactionNameAccount';
import TransactionCategoryCell from 'src/components/atomic/molecules/transactionCategoryCell/TransactionCategoryCell';
import TransactionFrequencyCell from 'src/components/atomic/molecules/transactionFrequencyCell/TransactionFrequencyCell';
import TransactionAmount from 'src/components/atomic/atoms/transactionAmount/TransactionAmount';
import { DlsTypographyVariants } from 'src/types/dls-typography-variants.enum';
import ApproveTransactionButton from 'src/components/atomic/atoms/approveTransactionButton/ApproveTransactionButton';
import EditTransactionButton from 'src/components/atomic/atoms/editTransactionButton/EditTransactionButton';
import * as Yup from 'yup';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useReviewTransaction } from 'src/hooks/queries/transactions';
import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { size } from 'lodash';
import { Categories } from 'src/services/category.service';
import AutoSaveTransaction from 'src/components/auto-save/AutoSaveTransaction';
import LiteralsService from 'src/services/literals/LiteralsService';
import { Alert } from '@americanexpress/dls-react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { CategoryModel } from 'src/types/category.model';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXp from 'src/types/one-xp';
import RulePromptWrapper, { RulePromptState } from 'src/components/atomic/molecules/rulePrompt/RulePrompt';
import { shouldShowRulePromptForTransactionTable } from 'src/utils/transactions.util';
import { useUserAttributes } from 'src/hooks/queries/users';

interface TransactionReviewTableCardFeatureWrapperProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    displayingTransactionRulePrompt?: TransactionModel;
    setDisplayingTransactionRulePrompt?: (transaction: TransactionModel) => void;
}

export default function TransactionReviewTableCardFeatureWrapper({
    transaction,
    categories,
    rowIndex,
    rulePromptState,
    setRulePromptState,
    displayingTransactionRulePrompt,
    setDisplayingTransactionRulePrompt
}: TransactionReviewTableCardFeatureWrapperProps): JSX.Element {
    const { data: transactionsContextualRulesFeatureFlag } = useFeatureFlag(
        oneXp.FeatureFlags.TransactionsContextualRules
    );

    return (
        <TransactionReviewTableCardDataWrapper
            transaction={transaction}
            categories={categories}
            rowIndex={rowIndex}
            rulePromptState={rulePromptState}
            setRulePromptState={setRulePromptState}
            displayingTransactionRulePrompt={displayingTransactionRulePrompt}
            setDisplayingTransactionRulePrompt={setDisplayingTransactionRulePrompt}
            featureFlagOn={transactionsContextualRulesFeatureFlag}
        />
    );
}

export interface TransactionReviewTableCardDataWrapperProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    displayingTransactionRulePrompt?: TransactionModel;
    setDisplayingTransactionRulePrompt?: (transaction: TransactionModel) => void;
    featureFlagOn?: boolean;
}

export function TransactionReviewTableCardDataWrapper({
    transaction,
    categories,
    rowIndex,
    rulePromptState,
    setRulePromptState,
    displayingTransactionRulePrompt,
    setDisplayingTransactionRulePrompt,
    featureFlagOn
}: TransactionReviewTableCardDataWrapperProps): JSX.Element {
    const { pathname } = useLocation();
    const { isMinWidthSmall } = useBreakpoints();
    const { data: userAttributes } = useUserAttributes();
    const { mutateAsync: reviewTransaction } = useReviewTransaction();
    const showTransactionRulePrompt =
        featureFlagOn &&
        shouldShowRulePromptForTransactionTable(
            displayingTransactionRulePrompt?.id,
            transaction.id,
            userAttributes,
            rulePromptState
        );

    const handleSubmitReviewTransaction = async (): Promise<void> => {
        trackClick(pathname, adobeTags.Transactions.Row.Approve);
        await reviewTransaction(transaction.id);
    };

    const handleCategoryChange = (groupUid: string): void => {
        if (setDisplayingTransactionRulePrompt) setDisplayingTransactionRulePrompt({ ...transaction, groupUid });
    };
    const handleCloseRulePrompt = (): void => {
        if (setDisplayingTransactionRulePrompt) setDisplayingTransactionRulePrompt(undefined);
    };

    return (
        <Formik
            initialValues={{ transaction }}
            validationSchema={Yup.object({
                transaction: Yup.object().shape({
                    groupUid: Yup.string().test('needsCategory', (value) => {
                        return value !== Categories.UncategorizedGroupId;
                    })
                })
            })}
            enableReinitialize
            validateOnBlur={false}
            onSubmit={handleSubmitReviewTransaction}
        >
            {({ handleSubmit, errors }) => {
                const hasError = size(errors) > 0;
                const baseFieldId = 'transaction';
                return (
                    <TransactionReviewTableCard
                        transaction={transaction}
                        categories={categories}
                        rowIndex={rowIndex}
                        handleSubmit={handleSubmit}
                        baseFieldId={baseFieldId}
                        hasError={hasError}
                        isMinWidthSmall={isMinWidthSmall}
                        showTransactionRulePrompt={showTransactionRulePrompt}
                        displayingTransactionRulePrompt={displayingTransactionRulePrompt}
                        handleCategoryChange={handleCategoryChange}
                        handleCloseRulePrompt={handleCloseRulePrompt}
                        rulePromptState={rulePromptState}
                        setRulePromptState={setRulePromptState}
                    />
                );
            }}
        </Formik>
    );
}

export interface TransactionReviewTableCardProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    handleSubmit: () => void;
    baseFieldId: string;
    hasError?: boolean;
    isMinWidthSmall?: boolean;
    showTransactionRulePrompt?: boolean;
    displayingTransactionRulePrompt?: TransactionModel;
    handleCategoryChange?: (groupUid: string) => void;
    handleCloseRulePrompt?: () => void;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
}

export function TransactionReviewTableCard({
    transaction,
    categories,
    rowIndex,
    handleSubmit,
    baseFieldId,
    hasError,
    isMinWidthSmall,
    showTransactionRulePrompt,
    displayingTransactionRulePrompt,
    handleCategoryChange,
    handleCloseRulePrompt,
    rulePromptState,
    setRulePromptState
}: TransactionReviewTableCardProps): JSX.Element {
    return (
        <div
            id={`transactionReviewTableCard-${rowIndex}-${transaction.id}`}
            className={styles.card}
            data-cy={Selectors.Transactions.REVIEW_ROW}
        >
            <TransactionDate date={transaction.date} />
            <div className={styles.cardSecondaryRow}>
                <div className={styles.nameAccountAmount}>
                    <TransactionNameAccount
                        transactionId={transaction.id}
                        accountId={transaction.accountId}
                        transactionDisplayName={transaction.displayName}
                        transactionDate={transaction.date}
                        merchantName={transaction.merchantName}
                    />
                    <TransactionAmount amount={transaction.amount} variant={DlsTypographyVariants.HEADING_4} />
                </div>
                {isMinWidthSmall && (
                    <div className={styles.cardInlineButtons}>
                        <EditTransactionButton
                            transactionId={transaction.id}
                            transactionName={transaction.displayName}
                            withText
                            className={styles.editButton}
                        />
                        <ApproveTransactionButton
                            transactionName={transaction.displayName}
                            withText
                            className={styles.approveButton}
                            onClick={handleSubmit}
                        />
                    </div>
                )}
            </div>
            <div className={styles.dropdowns}>
                <TransactionCategoryCell
                    transaction={transaction}
                    categories={categories}
                    baseFieldId={baseFieldId}
                    isReview
                    handleCategoryChange={handleCategoryChange}
                />
                {hasError && !isMinWidthSmall && (
                    <Alert
                        id={`${baseFieldId}.groupUidAlert`}
                        data-cy={`${baseFieldId}.groupUid${Selectors.SpendSmart.SELECT_DETAILED_ERROR}`}
                    >
                        {LiteralsService.getJsxLiteral('TransactionsTable.approveCategoryError', [
                            transaction.displayName
                        ])}
                    </Alert>
                )}
                {showTransactionRulePrompt && !isMinWidthSmall && (
                    <div className={styles.rulePromptMargin}>
                        <RulePromptWrapper
                            transaction={displayingTransactionRulePrompt}
                            onClose={handleCloseRulePrompt}
                            state={rulePromptState}
                            setState={setRulePromptState}
                        />
                    </div>
                )}
                <TransactionFrequencyCell baseFieldId={baseFieldId} isReview />
            </div>
            {hasError && isMinWidthSmall && (
                <Alert
                    id={`${baseFieldId}.groupUidAlert`}
                    data-cy={`${baseFieldId}.groupUid${Selectors.SpendSmart.SELECT_DETAILED_ERROR}`}
                    className={styles.alert}
                >
                    {LiteralsService.getJsxLiteral('TransactionsTable.approveCategoryError', [transaction.displayName])}
                </Alert>
            )}
            {showTransactionRulePrompt && isMinWidthSmall && (
                <div className={styles.rulePromptMargin}>
                    <RulePromptWrapper
                        transaction={displayingTransactionRulePrompt}
                        onClose={handleCloseRulePrompt}
                        state={rulePromptState}
                        setState={setRulePromptState}
                    />
                </div>
            )}
            {!isMinWidthSmall && (
                <div className={styles.cardButtons}>
                    <EditTransactionButton
                        transactionId={transaction.id}
                        transactionName={transaction.displayName}
                        withText
                        className={styles.editButton}
                    />
                    <ApproveTransactionButton
                        transactionName={transaction.displayName}
                        withText
                        className={styles.approveButton}
                        onClick={handleSubmit}
                    />
                </div>
            )}
            <AutoSaveTransaction
                transaction={transaction}
                baseFieldId={`${baseFieldId}.`}
                blockAutoSubmit
                handleCategoryChange={handleCategoryChange}
            />
        </div>
    );
}
